<form [formGroup]="form" class="p-(--space-3) h-full" #formContainer tabindex="-1">
  <div class="flex flex-col gap-(--gap-relaxed) h-full">
    <!-- ID du vérificateur -->
    @if (this.entity && t.testMode()) {
      <div class="field">#ID : {{ this.entity.id }}</div>
    }

    <!-- Type de vérificateur -->
    <div class="field">
      <ces-select-field
        [placeholder]="'common.placeholders.select' | transloco"
        [required]="true"
        formControlName="type"
        id="type"
        [label]="'common.labels.verificationType' | transloco"
        optionLabel="name"
        optionValue="value"
        [options]="CheckerOptions"
        styleClass="w-full cursor-default text-sm font-medium mb-(--space-1)" />
    </div>

    <!-- Dates de validité -->
    <div class="flex flex-row gap-(--gap-loose)">
      <div class="field w-1/2">
        <label for="startTs" class="cursor-default block text-sm font-medium mb-(--space-1)"
          >{{ 'forms.validityStart' | transloco }}<sup>*</sup></label
        >
        @if (t.testMode()) {
          <div class="text-xs">{{ form.get('startTs')?.value | date: 'dd.MM.yyyy HH:mm' }}</div>
        }
        <ces-datepicker id="startTs" formControlName="startTs" [autofocus]="false" class="w-full" />
        @if (isFieldInvalid('startTs')) {
          <small class="text-red-500">
            {{ getErrorMessage('startTs') }}
          </small>
        }
      </div>

      @if (t.testMode()) {
        <div class="field w-1/2">
          <label for="endTs" class="cursor-default block text-sm font-medium mb-(--space-1)">{{
            'forms.validityEnd' | transloco
          }}</label>
          <div class="text-xs">{{ form.get('endTs')?.value | date: 'dd.MM.yyyy HH:mm' }}</div>
          <ces-datepicker id="endTs" formControlName="endTs" [autofocus]="false" class="w-full" />
        </div>
      }
    </div>

    <!-- Commentaire -->
    @if (t.testMode()) {
      <div class="field">
        <ces-text-field
          id="comment"
          formControlName="comment"
          class="w-full"
          [rows]="2"
          [label]="'common.labels.comment' | transloco"
          [placeholder]="'forms.optionalComment' | transloco" />
      </div>
    }

    <!-- Contenu spécifique au type de vérificateur -->
    @if (form.get('type')?.value === CheckerType.TWO_FIXED_LIMITS) {
      <!-- Écarts fixes (type 1) -->
      <div class="field flex flex-1 w-full">
        <div class="flex flex-row w-full gap-(--gap-loose) justify-between">
          <div class="field w-1/2 flex-col flex">
            <label for="negativeGap" class="cursor-default block text-sm font-medium mb-(--space-1)"
              >{{ 'forms.lowerBound' | transloco }}<sup>*</sup></label
            >
            <div class="mt-auto">
              <p-inputnumber
                id="negativeGap"
                formControlName="negativeGap"
                [showButtons]="true"
                [maxFractionDigits]="4"
                (onKeyDown)="onDecimalKeyDown($event)"
                inputId="negativeGap" />
              @if (isFieldInvalid('negativeGap')) {
                <small class="text-red-500">
                  {{ getErrorMessage('negativeGap') }}
                </small>
              }
            </div>
          </div>
          <div class="field w-1/2 flex-col flex">
            <label for="positiveGap" class="cursor-default block text-sm font-medium mb-(--space-1)"
              >{{ 'forms.upperBound' | transloco }}<sup>*</sup></label
            >
            <div class="mt-auto">
              <p-inputnumber
                id="positiveGap"
                formControlName="positiveGap"
                [showButtons]="true"
                [maxFractionDigits]="4"
                (onKeyDown)="onDecimalKeyDown($event)"
                inputId="positiveGap" />
              @if (isFieldInvalid('positiveGap')) {
                <small class="text-red-500">
                  {{ getErrorMessage('positiveGap') }}
                </small>
              }
            </div>
          </div>
        </div>
      </div>
    }
    @if (form.get('type')?.value === CheckerType.TWO_POLYNOMIALS) {
      <!-- Formules (type 2) -->
      <p-tabs [value]="polynomialsActiveTab">
        <p-tablist>
          <p-tab value="tab1">{{ 'forms.lowerPolynomial' | transloco }}</p-tab>
          <p-tab value="tab2">{{ 'forms.upperPolynomial' | transloco }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="tab1">
            <ces-formula-model-buttons
              (loadClick)="openLoadFirstPolynomialModelDialog()"
              (saveClick)="openSaveFirstPolynomialModelDialog()"
              containerClass="mb-(--space-4)" />
            <div class="field">
              @if (formulaFirstControl.enabled) {
                <div>
                  <div class="text-label mb-(--space-1)">{{ 'common.labels.parameters' | transloco }}</div>
                  <ces-parameters-input
                    formControlName="formulaFirstParameters"
                    [units]="units"
                    [measures]="measures"
                    [mode]="ParameterMode.POLYNOMIAL"
                    (parametersChange)="onFirstParametersChange($event)" />
                  <div class="h-48 mt-(--space-4)">
                    <div class="text-label mb-(--space-1)">{{ 'forms.formula' | transloco }}</div>
                    <ces-editor
                      formControlName="formulaFirstExpression"
                      [variables]="firstEditorVariables"
                      [autoFormat]="true"
                      [showLineNumbers]="true" />
                  </div>
                  @if (isFieldInvalid('formulaFirstExpression')) {
                    <small class="text-red-500">
                      {{ getErrorMessage('formulaFirstExpression') }}
                    </small>
                  }
                </div>
              }
            </div>
          </p-tabpanel>
          <p-tabpanel value="tab2">
            <ces-formula-model-buttons
              (loadClick)="openLoadSecondPolynomialModelDialog()"
              (saveClick)="openSaveSecondPolynomialModelDialog()"
              containerClass="mb-(--space-4)" />
            <div class="field">
              @if (formulaSecondControl.enabled) {
                <div>
                  <div class="text-label mb-(--space-1)">{{ 'common.labels.parameters' | transloco }}</div>
                  <ces-parameters-input
                    formControlName="formulaSecondParameters"
                    [units]="units"
                    [measures]="measures"
                    [mode]="ParameterMode.POLYNOMIAL"
                    (parametersChange)="onSecondParametersChange($event)" />
                  <div class="h-48 mt-(--space-4)">
                    <div class="text-label mb-(--space-1)">{{ 'forms.formula' | transloco }}</div>
                    <ces-editor
                      formControlName="formulaSecondExpression"
                      [variables]="secondEditorVariables"
                      [autoFormat]="true"
                      [showLineNumbers]="true" />
                  </div>
                  @if (isFieldInvalid('formulaSecondExpression')) {
                    <small class="text-red-500">
                      {{ getErrorMessage('formulaSecondExpression') }}
                    </small>
                  }
                </div>
              }
            </div>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    }
    @if (form.get('type')?.value === CheckerType.POLYNOMIAL_AND_RANGES) {
      <!-- Polynôme et des plages d'écart (type 3) -->
      <p-tabs [value]="polynomialAndGapActiveTab">
        <p-tablist>
          <p-tab value="poly">{{ 'forms.polynomial' | transloco }}</p-tab>
          <p-tab value="gap">{{ 'forms.gapRanges' | transloco }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="poly">
            <ces-formula-model-buttons
              (loadClick)="openLoadFirstPolynomialModelDialog()"
              (saveClick)="openSaveFirstPolynomialModelDialog()"
              containerClass="mb-(--space-4)" />
            <div class="field">
              @if (formulaFirstControl.enabled) {
                <div>
                  <div class="text-label mb-(--space-1)">{{ 'common.labels.parameters' | transloco }}</div>
                  <ces-parameters-input
                    formControlName="formulaFirstParameters"
                    [units]="units"
                    [measures]="measures"
                    [mode]="ParameterMode.POLYNOMIAL"
                    (parametersChange)="onFirstParametersChange($event)" />
                  <div class="h-48 mt-(--space-4)">
                    <div class="text-label mb-(--space-1)">{{ 'forms.formula' | transloco }}</div>
                    <ces-editor
                      formControlName="formulaFirstExpression"
                      [variables]="firstEditorVariables"
                      [autoFormat]="true"
                      [showLineNumbers]="true" />
                  </div>
                  @if (isFieldInvalid('formulaFirstExpression')) {
                    <small class="text-red-500">
                      {{ getErrorMessage('formulaFirstExpression') }}
                    </small>
                  }
                </div>
              }
            </div>
          </p-tabpanel>
          <p-tabpanel value="gap">
            <div class="field">
              <div class="flex flex-row gap-(--gap-loose)">
                <div class="field w-1/2 flex flex-col">
                  <label for="negativeGap" class="cursor-default block text-sm font-medium mb-(--space-1)"
                    >{{ 'forms.negativeGap' | transloco }}<sup>*</sup></label
                  >
                  <span class="block text-muted-color text-xs mb-(--space-1)">({{ 'forms.absoluteValueHint' | transloco }})</span>
                  <div class="mt-auto">
                    <p-inputnumber
                      id="negativeGap"
                      formControlName="negativeGap"
                      [showButtons]="true"
                      [min]="0"
                      [maxFractionDigits]="4"
                      (onKeyDown)="onDecimalKeyDown($event)"
                      inputId="negativeGap" />
                    @if (isFieldInvalid('negativeGap')) {
                      <small class="text-red-500">
                        {{ getErrorMessage('negativeGap') }}
                      </small>
                    }
                  </div>
                </div>
                <div class="field w-1/2 flex flex-col">
                  <label for="positiveGap" class="cursor-default block text-sm font-medium mb-(--space-1)"
                    >{{ 'forms.positiveGap' | transloco }}<sup>*</sup></label
                  >
                  <div class="mt-auto">
                    <p-inputnumber
                      id="positiveGap"
                      formControlName="positiveGap"
                      [showButtons]="true"
                      [min]="0"
                      [maxFractionDigits]="4"
                      (onKeyDown)="onDecimalKeyDown($event)"
                      inputId="positiveGap" />
                    @if (isFieldInvalid('positiveGap')) {
                      <small class="text-red-500">
                        {{ getErrorMessage('positiveGap') }}
                      </small>
                    }
                  </div>
                </div>
              </div>
            </div>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    }

    @if (errorMessage) {
      <div class="text-red-500 mb-(--space-3)">
        {{ errorMessage }}
      </div>
    }

    <ces-dialog-footer [submitDisabled]="form.invalid || submitting" (onCancel)="onCancel()" (onSubmit)="onSubmit()">
      @if (mode === 'edit' && entity) {
        <ces-delete-button
          footer-start
          [label]="'entities.measureChecker.messages.deleteButton' | transloco"
          (confirm)="onDelete()"
          [confirmationConfig]="getDeleteConfirmationConfig()" />
      }
    </ces-dialog-footer>
  </div>
</form>
